import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { LoadErrorBox } from "../../components/common/LoadErrorBox";
import {
  BackLink, Section, SectionLabel, Card, CardTitle, KvGrid, KvItem, StatsRow, StatCard,
  Tabs, TabPanel, FooterActions, ActionButton, Alert, EmptyState, Divider,
} from "../../components/result/Primitives";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import { Edit2, Check, X, Settings, Save, RefreshCw, History } from "lucide-react";
import { toast } from "sonner";

const fmtEur = (cents) => `€ ${(Number(cents || 0) / 100).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtEurNoDec = (cents) => `€ ${(Number(cents || 0) / 100).toLocaleString("it-IT", { maximumFractionDigits: 0 })}`;

const PLAN_BADGE = {
  internal: "bg-subtle text-text-secondary",
  professional: "bg-accent-subtle text-accent-strong",
  business: "bg-accent-subtle text-accent-strong",
  enterprise: "bg-brand/10 text-text-primary",
};

export default function SuperAdminPricing() {
  const [plans, setPlans] = useState([]);
  const [costs, setCosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editPlan, setEditPlan] = useState(null);

  const [failed, setFailed] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([
        api.get("/superadmin/pricing-plans"),
        api.get("/superadmin/service-costs"),
      ]);
      setPlans(p.data);
      setCosts(c.data);
      setFailed(false);
    } catch {
      setFailed(true);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <BackLink to="/superadmin/organizations" label="Wattline" />
          <h1 className="text-3xl font-semibold text-text-primary tracking-tight">Piani e prezzi</h1>
          <p className="text-base text-text-secondary mt-2">Gestisci listino pubblico e configurazione pricing della piattaforma</p>
        </div>
        <Button variant="outline" className="rounded-md border-border h-10" onClick={() => toast.info("Storico modifiche disponibile a breve")}>
          <History size={14} className="mr-1.5" /> Storico modifiche
        </Button>
      </div>

      <Tabs defaultTab="plans" tabs={[
        { id: "plans", label: "Piani standard" },
        { id: "costs", label: "Costi OpenAPI" },
        { id: "overrides", label: "Override per cliente" },
      ]}>
        <TabPanel id="plans">
          {loading ? <div className="text-sm text-text-muted">Caricamento…</div> :
            failed ? <LoadErrorBox what="i piani" onRetry={load} testId="sa-pricing-error" /> :
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="plans-grid">
              {plans.map((p) => <PlanCard key={p.plan_id} plan={p} onEdit={() => setEditPlan(p)} />)}
            </div>
          }
        </TabPanel>

        <TabPanel id="costs">
          <ServiceCostsTable costs={costs} onUpdate={load} />
        </TabPanel>

        <TabPanel id="overrides">
          <EmptyState
            icon={Settings}
            title="Pricing personalizzato non ancora attivo"
            description="Quando avrai clienti che richiedono pricing custom (es. sconti negoziati), potrai configurarli qui per ogni cliente."
            primaryAction={{
              label: "Richiedi attivazione feature",
              onClick: () => toast.info("Feature disponibile on-demand. Contatta lo sviluppo per attivazione."),
            }}
          />
        </TabPanel>
      </Tabs>

      <EditPlanDialog plan={editPlan} onClose={() => setEditPlan(null)} onSaved={load} />
    </div>
  );
}

const PlanCard = ({ plan, onEdit }) => (
  <div className="bg-surface border border-border rounded-xl p-5 flex flex-col" data-testid={`plan-card-${plan.plan_id}`}
       style={plan.legacy ? { opacity: 0.7 } : undefined}>
    <div className="flex items-center gap-2">
      <div className={`inline-flex px-2 py-0.5 rounded-md text-[11px] font-medium ${PLAN_BADGE[plan.plan_id] || "bg-subtle"}`}>
        {plan.legacy ? "Legacy — non in vendita" : plan.is_public ? "Pubblico" : "Privato"}
      </div>
      {plan.legacy && (
        <span className="text-[10px] text-text-muted" data-testid={`plan-legacy-note-${plan.plan_id}`}>
          listino attivo: self-service annuale
        </span>
      )}
    </div>
    <h3 className="text-xl font-semibold text-text-primary mt-3">{plan.display_name}</h3>
    <div className="mt-4">
      <div className="text-2xl font-bold text-text-primary">{fmtEurNoDec(plan.monthly_fee_cents)}<span className="text-sm font-normal text-text-secondary">/mese</span></div>
      <div className="text-xs text-text-muted mt-1">+ {fmtEurNoDec(plan.setup_fee_cents)} setup fee</div>
    </div>
    <Divider />
    <div className="text-sm text-text-secondary space-y-1">
      <div><span className="font-semibold text-text-primary">{plan.monthly_quota_included.toLocaleString("it-IT")}</span> ricerche incluse</div>
      <div>{fmtEur(plan.extra_price_cents)} per ricerca extra</div>
    </div>
    {plan.description && <p className="text-xs text-text-secondary italic mt-3 leading-relaxed">{plan.description}</p>}
    <ul className="text-xs text-text-secondary mt-4 space-y-1.5 flex-1">
      {(plan.features || []).map((f, i) => (
        <li key={i} className="flex items-start gap-1.5">
          <Check size={12} className="text-success-strong mt-0.5 flex-shrink-0" />
          <span>{f}</span>
        </li>
      ))}
    </ul>
    <Button variant="outline" onClick={onEdit} className="rounded-md border-border mt-4" data-testid={`edit-plan-${plan.plan_id}`}>
      <Edit2 size={13} className="mr-1.5" /> Modifica piano
    </Button>
  </div>
);

const EditPlanDialog = ({ plan, onClose, onSaved }) => {
  const [form, setForm] = useState({});
  useEffect(() => { if (plan) setForm({ ...plan, features_text: (plan.features || []).join("\n") }); }, [plan]);
  if (!plan) return null;

  const save = async () => {
    try {
      const payload = {
        display_name: form.display_name,
        monthly_fee_cents: parseInt(form.monthly_fee_cents) || 0,
        monthly_quota_included: parseInt(form.monthly_quota_included) || 0,
        extra_price_cents: parseInt(form.extra_price_cents) || 0,
        setup_fee_cents: parseInt(form.setup_fee_cents) || 0,
        description: form.description,
        is_public: !!form.is_public,
        is_active: !!form.is_active,
        features: (form.features_text || "").split("\n").map(s => s.trim()).filter(Boolean),
      };
      await api.put(`/superadmin/pricing-plans/${plan.plan_id}`, payload);
      toast.success("Piano aggiornato");
      onClose(); onSaved();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Errore"); }
  };

  return (
    <Dialog open={!!plan} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-xl bg-surface sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Modifica {plan.display_name}</DialogTitle>
          <DialogDescription>Le modifiche al prezzo NON si applicano ai contratti esistenti.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <FormField label="Nome visualizzato" value={form.display_name} onChange={(v) => setForm({ ...form, display_name: v })} />
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Prezzo mensile (centesimi)" value={form.monthly_fee_cents} onChange={(v) => setForm({ ...form, monthly_fee_cents: v })} type="number" />
            <FormField label="Setup fee (centesimi)" value={form.setup_fee_cents} onChange={(v) => setForm({ ...form, setup_fee_cents: v })} type="number" />
            <FormField label="Ricerche incluse" value={form.monthly_quota_included} onChange={(v) => setForm({ ...form, monthly_quota_included: v })} type="number" />
            <FormField label="Prezzo extra (cent/ricerca)" value={form.extra_price_cents} onChange={(v) => setForm({ ...form, extra_price_cents: v })} type="number" />
          </div>
          <div>
            <Label className="text-xs font-medium text-text-secondary">Descrizione</Label>
            <textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2}
              className="mt-1.5 w-full px-3 py-2 text-sm rounded-md border border-border bg-surface" />
          </div>
          <div>
            <Label className="text-xs font-medium text-text-secondary">Features (una per riga)</Label>
            <textarea value={form.features_text || ""} onChange={(e) => setForm({ ...form, features_text: e.target.value })} rows={4}
              className="mt-1.5 w-full px-3 py-2 text-sm rounded-md border border-border bg-surface" />
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.is_public} onChange={(e) => setForm({ ...form, is_public: e.target.checked })} /> Pubblico</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Attivo</label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} className="bg-accent hover:bg-accent-strong text-white"><Save size={14} className="mr-1.5"/> Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const FormField = ({ label, value, onChange, type = "text" }) => (
  <div>
    <Label className="text-xs font-medium text-text-secondary">{label}</Label>
    <Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
      className="mt-1.5 h-10 rounded-md border-border bg-surface" />
  </div>
);

const ServiceCostsTable = ({ costs, onUpdate }) => {
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState("");

  const startEdit = (c) => { setEditingId(c.service_id); setDraft(String(c.cost_cents)); };
  const save = async (c) => {
    try {
      const newCents = parseInt(draft);
      if (isNaN(newCents) || newCents < 0) { toast.error("Valore non valido"); return; }
      await api.put(`/superadmin/service-costs/${c.service_id}`, { cost_cents: newCents });
      toast.success("Costo aggiornato");
      setEditingId(null); onUpdate();
    } catch (e) { toast.error("Errore"); }
  };

  return (
    <>
      <Card>
        <CardTitle>Costi OpenAPI per servizio</CardTitle>
        <div className="bg-surface rounded-md overflow-hidden">
          <table className="w-full text-sm" data-testid="costs-table">
            <thead className="bg-subtle text-xs uppercase tracking-wider text-text-secondary">
              <tr>
                <th className="text-left px-4 py-2.5 font-medium">Servizio</th>
                <th className="text-left px-4 py-2.5 font-medium">Categoria</th>
                <th className="text-right px-4 py-2.5 font-medium">Costo (cent)</th>
                <th className="text-left px-4 py-2.5 font-medium">Ultimo agg.</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {costs.map((c) => (
                <tr key={c.service_id} className="border-t border-border" data-testid={`cost-row-${c.service_id}`}>
                  <td className="px-4 py-2.5 text-text-primary">{c.name}</td>
                  <td className="px-4 py-2.5 text-text-secondary text-xs">{c.category}</td>
                  <td className="px-4 py-2.5 text-right">
                    {editingId === c.service_id ? (
                      <Input type="number" value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={() => save(c)} autoFocus
                        className="h-8 w-24 inline-block text-right" />
                    ) : (
                      <span className="font-mono">{c.cost_cents} cent · {fmtEur(c.cost_cents)}</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-text-muted">{c.last_updated_at ? new Date(c.last_updated_at).toLocaleString("it-IT") : "—"}</td>
                  <td className="px-4 py-2.5">
                    {editingId === c.service_id ? (
                      <button onClick={() => save(c)} className="text-success-strong p-1" title="Salva"><Check size={14}/></button>
                    ) : (
                      <button onClick={() => startEdit(c)} className="text-text-secondary hover:text-text-primary p-1" title="Modifica" data-testid={`edit-cost-${c.service_id}`}><Edit2 size={13}/></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <Section>
        <Alert variant="info">
          Questi sono i costi reali che paghi a OpenAPI per ogni chiamata. Modificali quando OpenAPI aggiorna i listini. Una modifica influenza il calcolo del margine ma NON i prezzi che fatturi ai clienti.
        </Alert>
      </Section>
    </>
  );
};
